import { debounce } from "lodash-es";
import { mix, transparentize } from "polished";
import { Component, createRef } from "react";
import { LoadingSpinner } from "widgets";

import { ColorSchemes, selectedColorScheme } from "../../app-utils/genome-browser-color.js";

export interface ITwoSetVennDiagramProps {
  onSelectionChange?: (numberOfSelectedItems: number, left: boolean, middle: boolean, right: boolean) => void;
  leftCount: number;
  intersectionCount: number;
  rightCount: number;
  isLoading: boolean;
}

interface ITwoSetVennDiagramState {
  left?: boolean;
  intersection?: boolean;
  right?: boolean;
  isLoading?: boolean;
}

interface ITwoSetVennColors {
  fillColorLeft: string;
  strokeColorLeft: string;
  fontColorLeft: string;
  fillColorIntersection: string;
  strokeColorIntersection: string;
  fontColorIntersection: string;
  fillColorRight: string;
  strokeColorRight: string;
  fontColorRight: string;
  fontStyleLeft: any;
  fontStyleIntersection: any;
  fontStyleRight: any;
}

const leftColorScheme = ColorSchemes[5];
const rightColorScheme = ColorSchemes[8];
const radius = 110;
const selectionColorMix = 0.7;
const selectionColor = transparentize(0.2, selectedColorScheme.medium);
const fontSize = 20;
const fontWidth = 11;
const strokeWidth = 2;

/**
 * A Venn diagram for visualisation of a two set comparison.
 * Labels show the cardinality of the left and right exclusive parts and intersection.
 *
 * Interactivity:
 * - each part (left, intersection, right) can be independently selected/deselected
 * - the sum of items in selected parts is reported via the callback 'onSelectionChange(numberOfSelectedItems:number)'
 *
 * created on 9/21/2017
 */
class TwoSetVennDiagram extends Component<ITwoSetVennDiagramProps, ITwoSetVennDiagramState> {
  state: ITwoSetVennDiagramState = {
    left: false,
    intersection: true,
    right: false,
    isLoading: false,
  };

  // update the state only if ref is defined to prevent react warnings for unmounted component
  // ref is only used to observe/detect if the component is mounted
  private ref = createRef<HTMLDivElement>();

  getSelectedCount(state: ITwoSetVennDiagramState) {
    if (this.props.leftCount == null || !this.props.intersectionCount == null || !this.props.rightCount == null) {
      return 0;
    }
    return (
      (state.left ? this.props.leftCount : 0) +
      (state.intersection ? this.props.intersectionCount : 0) +
      (state.right ? this.props.rightCount : 0)
    );
  }

  componentDidMount() {
    this.updateLoadingState = debounce(this.updateLoadingState, 300, { leading: false, trailing: true });
  }

  UNSAFE_componentWillReceiveProps(nextProps: ITwoSetVennDiagramProps, _nextState: ITwoSetVennDiagramState) {
    this.updateLoadingState(nextProps);
    if (
      nextProps.leftCount !== this.props.leftCount ||
      nextProps.intersectionCount !== this.props.intersectionCount ||
      nextProps.rightCount !== this.props.rightCount
    ) {
      if (this.ref.current) {
        this.setState({ left: false, intersection: true, right: false });
      }
      this.props.onSelectionChange(nextProps.intersectionCount, false, true, false);
    }
  }

  updateLoadingState = (props: ITwoSetVennDiagramProps) => {
    if (this.ref.current) {
      this.setState({ isLoading: props.isLoading });
    }
  };

  componentDidUpdate(prevProps: ITwoSetVennDiagramProps, prevState: ITwoSetVennDiagramState) {
    const oldCount = this.getSelectedCount(prevState);
    const newCount = this.getSelectedCount(this.state);
    if (
      (oldCount !== newCount ||
        prevState.left !== this.state.left ||
        prevState.intersection !== this.state.intersection ||
        prevState.right !== this.state.right) &&
      prevProps.onSelectionChange
    ) {
      prevProps.onSelectionChange(newCount, this.state.left, this.state.intersection, this.state.right);
    }
  }

  toggle(field: string) {
    if (this.ref.current) {
      this.setState((prevState: ITwoSetVennDiagramState) => ({ [field]: !prevState[field] }));
    }
  }

  render() {
    const {
      fillColorLeft,
      strokeColorLeft,
      fontColorLeft,
      fillColorIntersection,
      strokeColorIntersection,
      fontColorIntersection,
      fillColorRight,
      strokeColorRight,
      fontColorRight,
      fontStyleLeft,
      fontStyleIntersection,
      fontStyleRight,
    } = this.getColors();

    const leftCount = this.props.leftCount;
    const intersectionCount = this.props.intersectionCount;
    const rightCount = this.props.rightCount;
    const commonTextStyle = { fontFamily: "sans-serif", fontSize: fontSize };
    return (
      <div ref={this.ref}>
        <svg className="venn-diagram" width={3 * radius + 2 * strokeWidth} height={2 * radius + 2 * strokeWidth}>
          <g transform={`translate(${strokeWidth}, ${strokeWidth})`}>
            <defs>
              <clipPath id="clipCircleRight">
                <circle r={radius} cx={2 * radius} cy={radius} />
              </clipPath>
              <clipPath id="clipCircleLeft">
                <circle r={radius} cx={radius} cy={radius} />
              </clipPath>
            </defs>
            <circle
              id="left"
              r={radius}
              cx={radius}
              cy={radius}
              fill={fillColorLeft}
              stroke={strokeColorLeft}
              onClick={() => this.toggle("left")}
            />
            {/* Reducing the radius of the right circle by .2 to prevent color bleeding
             across area borders caused by anti aliasing */}
            <circle
              id="right"
              r={radius - 0.2}
              cx={2 * radius}
              cy={radius}
              fill={fillColorRight}
              stroke={strokeColorRight}
              onClick={() => this.toggle("right")}
            />
            {/** The following two circles represent the intersection part*/}
            <circle
              id="intersectionA"
              r={radius}
              cx={radius}
              cy={radius}
              stroke={strokeColorIntersection}
              fill={fillColorIntersection}
              clipPath="url(#clipCircleRight)"
              onClick={() => this.toggle("intersection")}
            />
            {/** Due to anti alialising artifacts, fill color of the circle is transparent,
             this draws only the left border of the intersecting area*/}
            <circle
              id="intersectionB"
              r={radius}
              cx={2 * radius}
              cy={radius}
              stroke={strokeColorIntersection}
              fill="rgba(0,0,0,0)"
              clipPath="url(#clipCircleLeft)"
              onClick={() => this.toggle("intersection")}
            />
            {/** left exclusive part label*/}
            {leftCount !== null && (
              <text
                x={0.5 * radius}
                y={radius}
                style={fontStyleLeft}
                fill={fontColorLeft}
                transform={`translate(${-0.5 * leftCount.toString().length * fontWidth}, ${0.5 * fontSize})`}
                {...commonTextStyle}
              >
                {leftCount}
              </text>
            )}
            {/** intersection label*/}
            {intersectionCount !== null && (
              <text
                x={1.5 * radius}
                y={radius}
                style={fontStyleIntersection}
                fill={fontColorIntersection}
                transform={`translate(${-0.5 * intersectionCount.toString().length * fontWidth}, ${0.5 * fontSize})`}
                {...commonTextStyle}
              >
                {intersectionCount}
              </text>
            )}
            {/** right exclusive part label*/}
            {rightCount !== null && (
              <text
                x={2.5 * radius}
                y={radius}
                style={fontStyleRight}
                fill={fontColorRight}
                transform={`translate(${-0.5 * rightCount.toString().length * fontWidth}, ${0.5 * fontSize})`}
                {...commonTextStyle}
              >
                {rightCount}
              </text>
            )}
          </g>
        </svg>
        {this.state.isLoading ? <LoadingSpinner inCenter /> : null}
      </div>
    );
  }

  getColors(): ITwoSetVennColors {
    const fillColorLeft = this.state.left
      ? mix(selectionColorMix, selectionColor, leftColorScheme.light)
      : leftColorScheme.light;
    const strokeColorLeft = this.state.left ? selectedColorScheme.medium : leftColorScheme.dark;
    const fontColorLeft = this.state.left ? selectedColorScheme.lightest : leftColorScheme.darker;

    const fillColorIntersection = this.state.intersection
      ? mix(selectionColorMix, selectionColor, mix(0.5, leftColorScheme.light, rightColorScheme.light))
      : mix(0.5, leftColorScheme.light, rightColorScheme.light);
    const strokeColorIntersection = this.state.intersection
      ? selectedColorScheme.medium
      : mix(0.5, leftColorScheme.dark, rightColorScheme.dark);
    const fontColorIntersection = this.state.intersection
      ? selectedColorScheme.lightest
      : mix(0.5, leftColorScheme.darker, rightColorScheme.darker);

    const fillColorRight = this.state.right
      ? mix(selectionColorMix, selectionColor, rightColorScheme.light)
      : rightColorScheme.light;
    const strokeColorRight = this.state.right ? selectedColorScheme.medium : rightColorScheme.dark;
    const fontColorRight = this.state.right ? selectedColorScheme.lightest : rightColorScheme.darker;

    const fontStyleLeft = this.state.left ? { fontWeight: "bold" } : null;
    const fontStyleIntersection = this.state.intersection ? { fontWeight: "bold" } : null;
    const fontStyleRight = this.state.right ? { fontWeight: "bold" } : null;

    return {
      fillColorLeft,
      strokeColorLeft,
      fontColorLeft,
      fillColorIntersection,
      strokeColorIntersection,
      fontColorIntersection,
      fillColorRight,
      strokeColorRight,
      fontColorRight,
      fontStyleLeft,
      fontStyleIntersection,
      fontStyleRight,
    };
  }
}

export default TwoSetVennDiagram;
